import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowSortDownLines16Regular,
  CalendarAdd16Regular,
  Call16Filled,
  Chat16Filled,
  Checkmark12Regular,
  ChevronUp16Regular,
  Dismiss12Regular,
  Filter16Regular,
  Mail16Filled,
  MoreHorizontal16Regular,
  PersonAdd16Regular,
  Search16Regular,
  Star16Filled,
  Star16Regular,
  Video16Filled,
} from "@fluentui/react-icons";
import type { FluentIcon } from "@fluentui/react-icons";
import styles from "./Contacts.module.css";
import type { ApiOptions } from "../dashboardApi";
import {
  canDial,
  createContact,
  createEvent,
  deleteContact as apiDeleteContact,
  deleteEvent as apiDeleteEvent,
  dialNumber,
  listContacts,
  listEvents,
  repeatEvent as apiRepeatEvent,
  setContactFavorite,
} from "../frontdeskApi";
import type { ContactDto, CreateContactBody, DialDefaults, ScheduledEventDto } from "../frontdeskApi";

/* ------------------------------------------------------------------ *
 *  Contacts tab (white theme) – lives inside the Front Desk modal.
 *  Four panels: groups · contact list · contact details · scheduled calls.
 *  Fills its parent (the Frontdesk body area, 900 × 485 design px).
 * ------------------------------------------------------------------ */

type Tag = "Friends" | "Work" | "Family" | "Important";
type Source = "Device" | "Gmail" | "iCloud" | "Outlook";
type GroupKind = "device" | "synced" | "shared";

interface Contact {
  id: string;
  name: string;
  company: string;
  role: string;
  tags: Tag[];
  favorite: boolean;
  source: Source;
  shared?: string | undefined;
  phones: { label: string; value: string }[];
  emails: { label: string; value: string }[];
  birthday: string;
  facebook: string;
  x: string;
  website: string;
}

interface CallEvent {
  id: string;
  title: string;
  desc?: string | undefined;
  date: Date;
  time: string; // HH:MM (24h)
  people: string[];
  extra: number;
  publicEvent?: boolean | undefined;
}

export interface ContactsProps {
  /** Extra search text (e.g. from the page header search). */
  query?: string;
  /** Gateway and sign-in (same object the dashboard uses). */
  api?: ApiOptions;
  /** Number and agent used by the Call button. Without it the Call button opens the phone app (tel:). */
  dial?: DialDefaults;
}

/* ---------- reference data ---------- */

/** Sidebar order of the groups; groups that are not listed here come after them, A to Z. Counts always come from the contacts. */
const COMPANY_ORDER = ["UBA Group", "Zenith Bank", "Access Bank", "GTBank", "Flutterwave", "Paystack", "MTN Nigeria", "Dangote Group"];
const SHARED_ORDER = ["Front Desk", "Sales Team", "Management"];
const SYNCED: Source[] = ["Gmail", "iCloud", "Outlook"];
const TAGS: Tag[] = ["Friends", "Work", "Family", "Important"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const NO_API: ApiOptions = {};

const ordered = (found: string[], hint: string[]) => {
  const set = new Set(found.filter(Boolean));
  const rest = [...set].filter((v) => !hint.includes(v)).sort((x, y) => x.localeCompare(y));
  return [...hint, ...rest];
};

const hash = (s: string) => {
  let h = 7;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};

const isTag = (t: string): t is Tag => (TAGS as string[]).includes(t);
const isSource = (t: string): t is Source => t === "Device" || t === "Gmail" || t === "iCloud" || t === "Outlook";

/** "1985-10-19" -> "October 19, 1985" */
const longDate = (isoDate: string | null) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate ?? "");
  return m ? `${MONTHS[Number(m[2]) - 1] ?? ""} ${Number(m[3])}, ${m[1] ?? ""}` : "";
};

const toContact = (d: ContactDto): Contact => ({
  id: d.id,
  name: d.name,
  company: d.company ?? "",
  role: d.role ?? "",
  tags: d.tags.filter(isTag),
  favorite: d.favorite,
  source: isSource(d.source) ? d.source : "Device",
  shared: d.shared ?? undefined,
  phones: d.phones,
  emails: d.emails,
  birthday: longDate(d.birthday),
  facebook: d.facebook ?? "",
  x: d.x ?? "",
  website: d.website ?? "",
});

/* ---------- date helpers ---------- */

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const pad = (n: number) => String(n).padStart(2, "0");
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIso = (s: string) => {
  const [y = 0, m = 1, d = 1] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const to12 = (t: string) => {
  const [h = 0, m = 0] = t.split(":").map(Number);
  return `${pad(h % 12 === 0 ? 12 : h % 12)}:${pad(m)} ${h < 12 ? "AM" : "PM"}`;
};

const toEvent = (d: ScheduledEventDto): CallEvent => ({
  id: d.id,
  title: d.title,
  desc: d.description ?? undefined,
  date: fromIso(d.date),
  time: d.time,
  people: d.people,
  extra: d.extra,
  publicEvent: d.isPublic || undefined,
});

const groupLabel = (d: Date, today: Date) => {
  const diff = Math.round((startOfDay(d).getTime() - startOfDay(today).getTime()) / 86400000);
  if (diff === 0) return "TODAY";
  if (diff === 1) return "TOMORROW";
  return `${d.getDate()} ${(MONTHS[d.getMonth()] ?? "").toUpperCase()}`;
};

/* ---------- small helpers ---------- */

const Avatar: React.FC<{ name: string; size: number; ring?: boolean }> = ({ name, size, ring }) => {
  const h = hash(name) % 360;
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
  return (
    <span
      className={`${styles.avatar} ${ring ? styles.avatarRing : ""}`}
      style={{ width: size, height: size, fontSize: size * 0.38, background: `linear-gradient(135deg, hsl(${h} 72% 64%), hsl(${(h + 35) % 360} 68% 50%))` }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
};

type IconName = "chev" | "search" | "sort" | "filter" | "dots" | "star" | "starO" | "phone" | "video" | "chat" | "mail" | "calPlus" | "userPlus" | "check" | "close";

/** Fluent UI icons (same icon set as the rest of the app) */
const ICONS: Record<IconName, FluentIcon> = {
  chev: ChevronUp16Regular,
  search: Search16Regular,
  sort: ArrowSortDownLines16Regular,
  filter: Filter16Regular,
  dots: MoreHorizontal16Regular,
  star: Star16Filled,
  starO: Star16Regular,
  phone: Call16Filled,
  video: Video16Filled,
  chat: Chat16Filled,
  mail: Mail16Filled,
  calPlus: CalendarAdd16Regular,
  userPlus: PersonAdd16Regular,
  check: Checkmark12Regular,
  close: Dismiss12Regular,
};

const Icon: React.FC<{ n: IconName; s?: number }> = ({ n, s = 9 }) => {
  const Cmp = ICONS[n];
  return <Cmp aria-hidden="true" style={{ width: s + 2, height: s + 2, flex: "none" }} />;
};

/* ---------- create-event panel (shared by "Schedule a call" and the calendar button) ---------- */

const CreateEventPanel: React.FC<{
  contacts: Contact[];
  initialTitle: string;
  initialPeople: string[];
  onCancel: () => void;
  onCreate: (title: string, desc: string, date: Date, time: string, people: string[]) => void;
}> = ({ contacts, initialTitle, initialPeople, onCancel, onCreate }) => {
  const [title, setTitle] = useState(initialTitle);
  const [desc, setDesc] = useState("");
  const [date, setDate] = useState(iso(addDays(new Date(), 1)));
  const [time, setTime] = useState("10:00");
  const [people, setPeople] = useState<string[]>(initialPeople);
  const [picker, setPicker] = useState(false);
  const [pq, setPq] = useState("");
  const pickerRef = useRef<HTMLDivElement>(null);
  const addBtnRef = useRef<HTMLButtonElement>(null);
  const ok = title.trim().length > 0 && date !== "" && time !== "";

  useEffect(() => {
    if (!picker) return;
    const h = (e: PointerEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPicker(false);
    };
    document.addEventListener("pointerdown", h);
    return () => document.removeEventListener("pointerdown", h);
  }, [picker]);

  const chosen = people.map((id) => contacts.find((c) => c.id === id)).filter((c): c is Contact => !!c);
  const available = contacts.filter((c) => !people.includes(c.id) && c.name.toLowerCase().includes(pq.trim().toLowerCase()));

  return (
    <form
      className={styles.cePanel}
      tabIndex={-1}
      role="dialog"
      aria-label="Create event"
      onSubmit={(e) => {
        e.preventDefault();
        if (ok) onCreate(title.trim(), desc.trim(), fromIso(date), time, people);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          e.nativeEvent.stopPropagation();
          if (picker) {
            setPicker(false);
            addBtnRef.current?.focus(); // keep focus inside the panel so the next Esc still closes only the panel
          } else onCancel();
        }
      }}
    >
      <div className={styles.ceHead}>
        <h3>Create event</h3>
        <button type="button" className={styles.round} aria-label="Close" onClick={onCancel}>
          <Icon n="close" s={8} />
        </button>
      </div>

      <div className={styles.ceBody}>
        <label className={styles.ceField}>
          <span className={styles.ceLabel}>TITLE</span>
          <input className={styles.ceInput} value={title} placeholder="Event title" autoFocus onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className={styles.ceField}>
          <span className={styles.ceLabel}>DESCRIPTION</span>
          <textarea className={`${styles.ceInput} ${styles.ceArea}`} value={desc} placeholder="What is this event about?" rows={4} onChange={(e) => setDesc(e.target.value)} />
        </label>
        <div className={styles.cePair}>
          <label className={styles.ceField}>
            <span className={styles.ceLabel}>DATE</span>
            <input className={styles.ceInput} type="date" value={date} min={iso(new Date())} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className={styles.ceField}>
            <span className={styles.ceLabel}>TIME</span>
            <input className={styles.ceInput} type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </label>
        </div>

        <div className={styles.ceField}>
          <span className={styles.ceLabel}>PARTICIPANTS</span>
          <div className={styles.ceBox}>
            <div className={styles.ceBoxHead} ref={pickerRef}>
              <span>Add people to the event</span>
              <button ref={addBtnRef} type="button" className={styles.round} aria-haspopup="listbox" aria-expanded={picker} aria-label="Add participant" title="Add participant" onClick={() => setPicker((o) => !o)}>
                <Icon n="userPlus" s={9} />
              </button>
              {picker && (
                <div className={styles.pop} style={{ right: 0, top: 21, width: 120 }}>
                  <input className={styles.ceInput} style={{ height: 14, marginBottom: 2 }} value={pq} placeholder="Search people" autoFocus aria-label="Search people" onChange={(e) => setPq(e.target.value)} />
                  <div className={styles.pickList} role="listbox" aria-label="People">
                    {available.length === 0 && <div className={styles.empty}>No one to add</div>}
                    {available.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        role="option"
                        aria-selected={false}
                        className={styles.pickItem}
                        onClick={() => {
                          setPeople((ps) => [...ps, c.id]);
                          setPq("");
                        }}
                      >
                        <Avatar name={c.name} size={11} />
                        <span>{c.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className={styles.ceList}>
              {chosen.length === 0 && <div className={styles.ceNone}>No participants yet</div>}
              {chosen.map((c) => (
                <div key={c.id} className={styles.cePerson}>
                  <Avatar name={c.name} size={15} />
                  <span className={styles.pname}>{c.name}</span>
                  <button type="button" className={styles.remove} aria-label={`Remove ${c.name}`} title="Remove" onClick={() => setPeople((ps) => ps.filter((x) => x !== c.id))}>
                    <i />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.ceFoot}>
        <button type="button" className={styles.btnGhost} onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className={styles.btnPrimary} disabled={!ok}>
          Create event
        </button>
      </div>
    </form>
  );
};

/* ---------- main component ---------- */

type PopId = "filter" | "more" | "detail" | `ev:${string}`;
type TagFilter = "All" | "Favorites" | Tag;

const Contacts: React.FC<ContactsProps> = ({ query, api, dial }) => {
  const apiOpts = api ?? NO_API;
  const today = useMemo(() => startOfDay(new Date()), []);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [events, setEvents] = useState<CallEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedId, setSelectedId] = useState("");
  const [group, setGroup] = useState<{ kind: GroupKind; key: string }>({ kind: "device", key: "all" });
  const [collapsed, setCollapsed] = useState<Record<GroupKind, boolean>>({ device: false, synced: false, shared: false });
  const [search, setSearch] = useState("");
  const [asc, setAsc] = useState(true);
  const [tagFilter, setTagFilter] = useState<TagFilter>("All");
  const [pop, setPop] = useState<PopId | null>(null);
  const [adding, setAdding] = useState(false);
  const [eventDraft, setEventDraft] = useState<{ title: string; people: string[] } | null>(null);
  const [toast, setToast] = useState<{ msg: string; id: number } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  const copyTimer = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      window.clearTimeout(toastTimer.current);
      window.clearTimeout(copyTimer.current);
    },
    [],
  );

  const showToast = useCallback((msg: string) => {
    window.clearTimeout(toastTimer.current);
    setToast({ msg, id: Date.now() });
    toastTimer.current = window.setTimeout(() => setToast(null), 2200);
  }, []);

  /* contacts and scheduled events come from the backend */
  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    Promise.all([listContacts(apiOpts, ac.signal), listEvents(apiOpts, ac.signal)])
      .then(([cs, es]) => {
        if (ac.signal.aborted) return;
        setContacts(cs.map(toContact));
        setEvents(es.map(toEvent));
        setLoadError(null);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setLoadError(e instanceof Error ? e.message : "Could not load contacts.");
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [apiOpts, reloadKey]);

  const fail = useCallback(
    (e: unknown) => showToast(e instanceof Error ? e.message : "Something went wrong."),
    [showToast],
  );

  /* close popovers on outside pointer-down */
  useEffect(() => {
    if (!pop) return;
    const h = (e: PointerEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el?.closest(`[data-pop="${pop}"]`)) setPop(null);
    };
    document.addEventListener("pointerdown", h);
    return () => document.removeEventListener("pointerdown", h);
  }, [pop]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Escape") return;
    if (adding || pop) {
      e.stopPropagation();
      e.nativeEvent.stopPropagation();
      if (adding) setAdding(false);
      else setPop(null);
    }
  };

  /* ----- derived data ----- */
  const companies = useMemo(() => ordered(contacts.map((c) => c.company), COMPANY_ORDER), [contacts]);
  const sharedGroups = useMemo(() => ordered(contacts.map((c) => c.shared ?? ""), SHARED_ORDER), [contacts]);

  const counts = useMemo(() => {
    const device: Record<string, number> = {};
    companies.forEach((c) => (device[c] = contacts.filter((x) => x.company === c).length));
    const synced: Record<string, number> = {};
    SYNCED.forEach((s) => (synced[s] = contacts.filter((x) => x.source === s).length));
    const shared: Record<string, number> = {};
    sharedGroups.forEach((g) => (shared[g] = contacts.filter((x) => x.shared === g).length));
    return {
      device,
      synced,
      shared,
      allDevice: contacts.length,
      allSynced: contacts.filter((x) => x.source !== "Device").length,
      allShared: contacts.filter((x) => x.shared).length,
    };
  }, [contacts, companies, sharedGroups]);

  const visible = useMemo(() => {
    const terms = [search, query ?? ""].map((t) => t.trim().toLowerCase()).filter(Boolean);
    const list = contacts.filter((c) => {
      const inGroup =
        group.kind === "device"
          ? group.key === "all" || c.company === group.key
          : group.kind === "synced"
            ? group.key === "all"
              ? c.source !== "Device"
              : c.source === group.key
            : group.key === "all"
              ? !!c.shared
              : c.shared === group.key;
      if (!inGroup) return false;
      if (tagFilter === "Favorites" ? !c.favorite : tagFilter !== "All" && !c.tags.includes(tagFilter)) return false;
      const hay = `${c.name} ${c.company} ${c.role} ${c.tags.join(" ")} ${c.phones.map((p) => p.value).join(" ")} ${c.emails.map((m) => m.value).join(" ")}`.toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
    list.sort((a, b) => (asc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)));
    return list;
  }, [contacts, group, tagFilter, search, query, asc]);

  const letters = useMemo(() => {
    const out: [string, Contact[]][] = [];
    visible.forEach((c) => {
      const L = c.name.charAt(0).toUpperCase();
      const last = out[out.length - 1];
      if (last && last[0] === L) last[1].push(c);
      else out.push([L, [c]]);
    });
    return out;
  }, [visible]);

  const selected = contacts.find((c) => c.id === selectedId) ?? visible[0] ?? contacts[0];

  const sortedEvents = useMemo(() => [...events].sort((a, b) => startOfDay(a.date).getTime() - startOfDay(b.date).getTime() || a.time.localeCompare(b.time)), [events]);
  const eventGroups = useMemo(() => {
    const out: { key: string; label: string; items: CallEvent[] }[] = [];
    sortedEvents.forEach((ev) => {
      const key = iso(ev.date);
      const last = out[out.length - 1];
      if (last && last.key === key) last.items.push(ev);
      else out.push({ key, label: groupLabel(ev.date, today), items: [ev] });
    });
    return out;
  }, [sortedEvents, today]);

  /* ----- actions (every change goes to the backend first) ----- */
  const toggleFavorite = (id: string) => {
    const current = contacts.find((c) => c.id === id);
    if (!current) return;
    const next = !current.favorite;
    setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, favorite: next } : c)));
    setContactFavorite(apiOpts, id, next).catch((e: unknown) => {
      setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, favorite: !next } : c)));
      fail(e);
    });
  };
  const deleteContact = (id: string) => {
    const idx = visible.findIndex((c) => c.id === id);
    const next = visible[idx + 1] ?? visible[idx - 1];
    apiDeleteContact(apiOpts, id)
      .then(() => {
        setContacts((cs) => cs.filter((c) => c.id !== id));
        setEvents((es) => es.map((ev) => ({ ...ev, people: ev.people.filter((p) => p !== id) })));
        setSelectedId(next?.id ?? "");
        setPop(null);
        showToast("Contact deleted");
      })
      .catch(fail);
  };
  const addEvent = (title: string, date: Date, time: string, people: string[], desc?: string | undefined) => {
    createEvent(apiOpts, { title, description: desc || undefined, date: iso(date), time, people })
      .then((ev) => {
        setEvents((es) => [...es, toEvent(ev)]);
        setPop(null);
        setEventDraft(null);
        showToast(`Scheduled for ${date.getDate()} ${MONTHS[date.getMonth()]?.slice(0, 3)} · ${to12(time)}`);
      })
      .catch(fail);
  };
  const repeatEvent = (id: string) => {
    apiRepeatEvent(apiOpts, id)
      .then((ev) => {
        setEvents((es) => [...es, toEvent(ev)]);
        setPop(null);
        showToast("Repeated next week");
      })
      .catch(fail);
  };
  const removeEvent = (id: string) => {
    apiDeleteEvent(apiOpts, id)
      .then(() => {
        setEvents((es) => es.filter((x) => x.id !== id));
        setPop(null);
        showToast("Event deleted");
      })
      .catch(fail);
  };
  const saveContact = async (body: CreateContactBody) => {
    const created = toContact(await createContact(apiOpts, body));
    setContacts((cs) => [...cs, created]);
    setSelectedId(created.id);
    setGroup({ kind: "device", key: "all" });
    setTagFilter("All");
    setSearch("");
    setAdding(false);
    showToast(`${created.name} added`);
  };
  const callContact = (c: Contact) => {
    const number = c.phones[0]?.value;
    if (!number) {
      showToast(`${c.name} has no phone number`);
      return;
    }
    if (canDial(dial)) {
      dialNumber(apiOpts, dial, { to: number })
        .then(() => showToast(`Calling ${c.name}…`))
        .catch(fail);
    } else {
      window.location.href = `tel:${number.replace(/[^+\d]/g, "")}`;
      showToast(`Calling ${c.name}…`);
    }
  };
  const emailContact = (c: Contact) => {
    const address = c.emails[0]?.value;
    if (!address) {
      showToast(`${c.name} has no email address`);
      return;
    }
    window.location.href = `mailto:${address}`;
    showToast(`Composing an email to ${c.name}…`);
  };
  const exportContacts = () => {
    const cell = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const rows = [
      ["Name", "Company", "Role", "Tags", "Favorite", "Source", "Shared", "Phones", "Emails", "Birthday"],
      ...visible.map((c) => [c.name, c.company, c.role, c.tags.join("; "), c.favorite ? "yes" : "no", c.source, c.shared ?? "", c.phones.map((p) => p.value).join("; "), c.emails.map((m) => m.value).join("; "), c.birthday]),
    ];
    const blob = new Blob([rows.map((r) => r.map(cell).join(",")).join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contacts.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    showToast(`Exported ${visible.length} contacts (contacts.csv)`);
  };
  const copy = (key: string, value: string) => {
    try {
      navigator.clipboard?.writeText(value).catch(() => undefined);
    } catch {
      /* clipboard may be unavailable */
    }
    setCopied(key);
    window.clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopied(null), 1100);
    showToast("Copied to clipboard");
  };
  const clearFilters = () => {
    setSearch("");
    setTagFilter("All");
    setGroup({ kind: "device", key: "all" });
    setPop(null);
  };

  /* ----- sidebar group ----- */
  const groupSection = (kind: GroupKind, title: string, all: number, items: [string, number][]) => (
    <div className={styles.gSection} key={kind}>
      <button type="button" className={styles.gHead} aria-expanded={!collapsed[kind]} onClick={() => setCollapsed((c) => ({ ...c, [kind]: !c[kind] }))}>
        <span>{title}</span>
        <span className={`${styles.chev} ${collapsed[kind] ? styles.chevDown : ""}`}>
          <Icon n="chev" s={8} />
        </span>
      </button>
      {!collapsed[kind] &&
        [["all", all] as [string, number], ...items].map(([key, n]) => {
          const on = group.kind === kind && group.key === key;
          return (
            <button key={key} type="button" className={`${styles.gItem} ${on ? styles.gItemOn : ""}`} aria-pressed={on} onClick={() => setGroup({ kind, key })}>
              <span>{key === "all" ? "All contacts" : key}</span>
              <span className={styles.count}>{n}</span>
            </button>
          );
        })}
    </div>
  );

  const filterActive = tagFilter !== "All";

  return (
    <div className={styles.root} tabIndex={-1} onKeyDown={onKeyDown}>
      {/* ───────── 1 · groups ───────── */}
      <aside className={styles.panel} aria-label="Contact groups">
        <div className={styles.gScroll}>
          {groupSection("device", "ON THIS DEVICE", counts.allDevice, companies.map((c): [string, number] => [c, counts.device[c] ?? 0]))}
          {groupSection("synced", "SYNCED SOURCES", counts.allSynced, SYNCED.map((x): [string, number] => [x, counts.synced[x] ?? 0]))}
          {groupSection("shared", "SHARED", counts.allShared, sharedGroups.map((x): [string, number] => [x, counts.shared[x] ?? 0]))}
        </div>
        <button type="button" className={styles.addBtn} onClick={() => setAdding(true)}>
          <Icon n="userPlus" s={10} />
          Add contact
        </button>
      </aside>

      {/* ───────── 2 · list ───────── */}
      <section className={styles.panel} aria-label="Contact list">
        <div className={styles.listHead}>
          <div className={styles.listLabel}>DISPLAYED CONTACTS · {visible.length}</div>
          <div className={styles.listTools}>
            <label className={styles.search}>
              <Icon n="search" s={8} />
              <input value={search} placeholder="Search" aria-label="Search contacts" onChange={(e) => setSearch(e.target.value)} />
              {search && (
                <button type="button" className={styles.clear} aria-label="Clear search" onClick={() => setSearch("")}>
                  ×
                </button>
              )}
            </label>
            <button type="button" className={styles.round} aria-label={asc ? "Sorted A to Z" : "Sorted Z to A"} title={asc ? "Sorted A → Z" : "Sorted Z → A"} onClick={() => setAsc((a) => !a)}>
              <Icon n="sort" s={9} />
            </button>
            <div className={styles.anchor} data-pop="filter">
              <button type="button" className={`${styles.round} ${filterActive ? styles.roundOn : ""}`} aria-haspopup="menu" aria-expanded={pop === "filter"} aria-label="Filter" title="Filter" onClick={() => setPop((p) => (p === "filter" ? null : "filter"))}>
                <Icon n="filter" s={9} />
                {filterActive && <i className={styles.dot} />}
              </button>
              {pop === "filter" && (
                <div className={styles.pop} style={{ right: 0, top: 21, width: 78 }} role="menu">
                  {(["All", "Favorites", ...TAGS] as TagFilter[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      role="menuitemradio"
                      aria-checked={tagFilter === t}
                      className={`${styles.popItem} ${tagFilter === t ? styles.popItemOn : ""}`}
                      onClick={() => {
                        setTagFilter(t);
                        setPop(null);
                      }}
                    >
                      <span>{t}</span>
                      {tagFilter === t && <Icon n="check" s={7} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.anchor} data-pop="more">
              <button type="button" className={styles.round} aria-haspopup="menu" aria-expanded={pop === "more"} aria-label="More" title="More" onClick={() => setPop((p) => (p === "more" ? null : "more"))}>
                <Icon n="dots" s={9} />
              </button>
              {pop === "more" && (
                <div className={styles.pop} style={{ right: 0, top: 21, width: 88 }} role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.popItem}
                    onClick={() => {
                      setAdding(true);
                      setPop(null);
                    }}
                  >
                    New contact
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.popItem}
                    onClick={() => {
                      setPop(null);
                      exportContacts();
                    }}
                  >
                    Export contacts
                  </button>
                  <button type="button" role="menuitem" className={styles.popItem} onClick={clearFilters}>
                    Clear filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.list} role="listbox" aria-label="Contacts">
          {visible.length === 0 && loading && <div className={styles.empty}>Loading contacts…</div>}
          {visible.length === 0 && !loading && loadError && (
            <div className={styles.empty}>
              {loadError}{" "}
              <button type="button" className={styles.btnGhost} onClick={() => setReloadKey((k) => k + 1)}>
                Try again
              </button>
            </div>
          )}
          {visible.length === 0 && !loading && !loadError && <div className={styles.empty}>No contacts found</div>}
          {letters.map(([letter, items]) => (
            <div key={letter}>
              <div className={styles.letter}>{letter}</div>
              {items.map((c) => {
                const on = c.id === selected?.id;
                return (
                  <div
                    key={c.id}
                    role="option"
                    aria-selected={on}
                    tabIndex={0}
                    className={`${styles.row} ${on ? styles.rowOn : ""}`}
                    onClick={() => setSelectedId(c.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedId(c.id);
                      }
                    }}
                  >
                    <Avatar name={c.name} size={27} />
                    <span className={styles.rowText}>
                      <span className={styles.rowTop}>
                        <b>{c.name}</b>
                        {c.tags.map((t) => (
                          <i key={t} className={`${styles.tag} ${styles["tag" + t]}`}>
                            {t}
                          </i>
                        ))}
                      </span>
                      <span className={styles.rowSub}>
                        {[c.company, c.role].filter(Boolean).join(", ")}
                      </span>
                    </span>
                    <button
                      type="button"
                      className={`${styles.star} ${c.favorite ? styles.starOn : ""}`}
                      aria-label={c.favorite ? `Remove ${c.name} from favorites` : `Add ${c.name} to favorites`}
                      aria-pressed={c.favorite}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(c.id);
                      }}
                    >
                      <Icon n={c.favorite ? "star" : "starO"} s={9} />
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </section>

      {/* ───────── 3 · details ───────── */}
      <section className={styles.panel} aria-label="Contact details">
        {selected ? (
          <>
            <div className={styles.dTop}>
              <div className={styles.dHead}>
                <Avatar name={selected.name} size={42} />
                <div className={styles.dInfo}>
                  <div className={styles.dName}>{selected.name}</div>
                  <div className={styles.dSub}>
                    {[selected.company, selected.role].filter(Boolean).join(", ")}
                  </div>
                  <div className={styles.dTags}>
                    {selected.tags.map((t) => (
                      <i key={t} className={`${styles.tag} ${styles["tag" + t]}`}>
                        {t}
                      </i>
                    ))}
                  </div>
                </div>
                <div className={`${styles.anchor} ${styles.dDots}`} data-pop="detail">
                  <button type="button" className={styles.plain} aria-haspopup="menu" aria-expanded={pop === "detail"} aria-label="Contact options" onClick={() => setPop((p) => (p === "detail" ? null : "detail"))}>
                    <Icon n="dots" s={10} />
                  </button>
                  {pop === "detail" && (
                    <div className={styles.pop} style={{ right: 0, top: 14, width: 92 }} role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        className={styles.popItem}
                        onClick={() => {
                          toggleFavorite(selected.id);
                          setPop(null);
                        }}
                      >
                        {selected.favorite ? "Remove favorite" : "Add to favorites"}
                      </button>
                      <button type="button" role="menuitem" className={`${styles.popItem} ${styles.popDanger}`} onClick={() => deleteContact(selected.id)}>
                        Delete contact
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.actions}>
                {(
                  [
                    ["phone", "Call", () => callContact(selected)],
                    ["video", "Video call", () => showToast(`Video calls are not available yet`)],
                    ["chat", "Message", () => showToast(`Messaging is not available yet`)],
                    ["mail", "Email", () => emailContact(selected)],
                  ] as [IconName, string, () => void][]
                ).map(([ic, label, run]) => (
                  <button key={ic} type="button" className={styles.action} aria-label={label} title={label} onClick={run}>
                    <Icon n={ic} s={10} />
                  </button>
                ))}
              </div>

              <button type="button" className={styles.schedule} aria-haspopup="dialog" onClick={() => setEventDraft({ title: `Call with ${selected.name}`, people: [selected.id] })}>
                <Icon n="calPlus" s={10} />
                Schedule a call
              </button>
            </div>

            <div className={styles.fields}>
              {(
                [
                  selected.phones.map((p) => [`Phone · ${p.label}`, p.label, p.value] as const),
                  selected.emails.map((m) => [`Email · ${m.label}`, m.label, m.value] as const),
                  [["Birthday", "Birthday", selected.birthday] as const],
                  [["Facebook", "Facebook", selected.facebook] as const, ["X", "X", selected.x] as const],
                  [["Website", "Website", selected.website] as const],
                ] as (readonly [string, string, string])[][]
              )
                .map((grp) => grp.filter(([, , value]) => value !== ""))
                .filter((grp) => grp.length > 0)
                .map((grp, gi) => (
                <div key={gi} className={styles.fGroup}>
                  {grp.map(([key, label, value]) => (
                    <button key={key} type="button" className={`${styles.field} ${copied === key + selected.id ? styles.fieldCopied : ""}`} title="Click to copy" onClick={() => copy(key + selected.id, value)}>
                      <span className={styles.fLabel}>{copied === key + selected.id ? "Copied!" : label}</span>
                      <span className={styles.fValue}>{value}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className={styles.empty}>Select a contact to see the details</div>
        )}
      </section>

      {/* ───────── 4 · scheduled events ───────── */}
      <section className={styles.panel} aria-label="Scheduled events">
        <div className={styles.eHead}>
          <h3>Scheduled events</h3>
          <button type="button" className={styles.round} aria-haspopup="dialog" aria-label="Add event" title="Add event" onClick={() => setEventDraft({ title: "", people: [] })}>
            <Icon n="calPlus" s={9} />
          </button>
        </div>
        <div className={styles.eList}>
          {eventGroups.length === 0 && <div className={styles.empty}>No scheduled events</div>}
          {eventGroups.map((g) => (
            <div key={g.key}>
              <div className={styles.eLabel}>{g.label}</div>
              {g.items.map((ev) => {
                const people = ev.people.map((id) => contacts.find((c) => c.id === id)).filter((c): c is Contact => !!c);
                const popId: PopId = `ev:${ev.id}`;
                return (
                  <article key={ev.id} className={styles.event}>
                    <div className={styles.evTop}>
                      <h4>{ev.title}</h4>
                      <div className={styles.anchor} data-pop={popId}>
                        <button type="button" className={styles.plain} aria-haspopup="menu" aria-expanded={pop === popId} aria-label={`Options for ${ev.title}`} onClick={() => setPop((p) => (p === popId ? null : popId))}>
                          <Icon n="dots" s={10} />
                        </button>
                        {pop === popId && (
                          <div className={styles.pop} style={{ right: 0, top: 14, width: 80 }} role="menu">
                            <button
                              type="button"
                              role="menuitem"
                              className={styles.popItem}
                              onClick={() => repeatEvent(ev.id)}
                            >
                              Repeat next week
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className={`${styles.popItem} ${styles.popDanger}`}
                              onClick={() => removeEvent(ev.id)}
                            >
                              Delete event
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {ev.desc && <p>{ev.desc}</p>}
                    <div className={styles.evBottom}>
                      {ev.publicEvent ? (
                        <i className={styles.publicTag}>Public event</i>
                      ) : (
                        <span className={styles.stack}>
                          {people.slice(0, 5).map((p) => (
                            <button key={p.id} type="button" className={styles.stackBtn} title={p.name} aria-label={`Show ${p.name}`} onClick={() => setSelectedId(p.id)}>
                              <Avatar name={p.name} size={15} ring />
                            </button>
                          ))}
                          {ev.extra > 0 && <span className={styles.more}>+{ev.extra}</span>}
                        </span>
                      )}
                      <span className={styles.chip}>
                        {MONTHS[ev.date.getMonth()]?.slice(0, 3)} {ev.date.getDate()} · {to12(ev.time)}
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
          ))}
        </div>
      </section>

      {/* ───────── create event panel ───────── */}
      {eventDraft && (
        <CreateEventPanel
          key={eventDraft.title + eventDraft.people.join()}
          contacts={contacts}
          initialTitle={eventDraft.title}
          initialPeople={eventDraft.people}
          onCancel={() => setEventDraft(null)}
          onCreate={(t, d, date, time, people) => addEvent(t, date, time, people, d)}
        />
      )}

      {/* ───────── add contact dialog ───────── */}
      {adding && (
        <AddContact companies={companies} onCancel={() => setAdding(false)} onSave={saveContact} />
      )}

      {toast && (
        <div key={toast.id} className={styles.toast} role="status">
          {toast.msg}
        </div>
      )}
    </div>
  );
};

/* ---------- add-contact dialog ---------- */

const AddContact: React.FC<{ companies: string[]; onCancel: () => void; onSave: (body: CreateContactBody) => Promise<void> }> = ({ companies, onCancel, onSave }) => {
  const [name, setName] = useState("");
  const [company, setCompany] = useState(companies[0] ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [tag, setTag] = useState<Tag>("Work");
  const ok = name.trim().length > 1 && !busy;
  return (
    <div
      className={styles.dialogWrap}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <form
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label="New contact"
        onSubmit={(e) => {
          e.preventDefault();
          if (!ok) return;
          setBusy(true);
          setError(null);
          onSave({ name: name.trim(), company: company.trim() || undefined, role: role.trim() || "Contact", tags: [tag], phone: phone.trim() || undefined, email: email.trim() || undefined, source: "Device" }).catch((err: unknown) => {
            setBusy(false);
            setError(err instanceof Error ? err.message : "Could not save the contact.");
          });
        }}
      >
        <h3>New contact</h3>
        <label className={styles.formRow}>
          <span>Full name *</span>
          <input className={styles.formInput} value={name} autoFocus placeholder="e.g. Ada Nwosu" onChange={(e) => setName(e.target.value)} />
        </label>
        <div className={styles.formPair}>
          <label className={styles.formRow}>
            <span>Company</span>
            {companies.length > 0 ? (
              <select className={styles.formInput} value={company} onChange={(e) => setCompany(e.target.value)}>
                {companies.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            ) : (
              <input className={styles.formInput} value={company} placeholder="Company" onChange={(e) => setCompany(e.target.value)} />
            )}
          </label>
          <label className={styles.formRow}>
            <span>Tag</span>
            <select className={styles.formInput} value={tag} onChange={(e) => setTag(e.target.value as Tag)}>
              {TAGS.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </label>
        </div>
        <label className={styles.formRow}>
          <span>Role</span>
          <input className={styles.formInput} value={role} placeholder="e.g. Branch manager" onChange={(e) => setRole(e.target.value)} />
        </label>
        <label className={styles.formRow}>
          <span>Phone</span>
          <input className={styles.formInput} value={phone} placeholder="+234 …" inputMode="tel" onChange={(e) => setPhone(e.target.value)} />
        </label>
        <label className={styles.formRow}>
          <span>Email</span>
          <input className={styles.formInput} type="email" value={email} placeholder="name@company.com" onChange={(e) => setEmail(e.target.value)} />
        </label>
        {error && (
          <div className={styles.empty} role="alert">
            {error}
          </div>
        )}
        <div className={styles.formBtns}>
          <button type="button" className={styles.btnGhost} onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className={styles.btnPrimary} disabled={!ok}>
            {busy ? "Saving…" : "Save contact"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Contacts;